import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { firstValueFrom, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Token } from '@core/models';

/**
 * Token Service
 * Manages JWT tokens for authentication (access and refresh)
 * Based on atlas-frontend token service
 */
@Injectable({ providedIn: 'root' })
export class TokenService {
  /* JWT Decoder */
  private readonly _jwtService: JwtHelperService = new JwtHelperService();

  /** Access Token */
  private _accessToken: string | null = null;
  public get accessToken(): string | null {
    if (this._accessToken !== null) return this._accessToken;
    const token = this._getSavedToken();
    this._accessToken = token?.access ?? null;
    return this._accessToken;
  }

  /* Refresh Token */
  private _refreshValid: boolean = false;
  private _refreshValidPromise$?: Promise<boolean>;
  private _refreshingPromise$: Promise<boolean> | undefined;
  public get refreshToken(): string | null {
    const token = this._getSavedToken();
    return token?.refresh ?? null;
  }

  constructor(private readonly http: HttpClient) {}

  /**
   * Saves the token in user session
   * @param {Token} token - Token to save
   */
  public saveToken(token: Token): void {
    this._accessToken = token.access;
    localStorage.setItem('token', JSON.stringify(token));
  }

  /**
   * Gets the saved token from localStorage
   * @returns {Token | null} - Saved token (or null if doesn't exist)
   */
  private _getSavedToken(): Token | null {
    const tokenJson = localStorage.getItem('token');
    if (tokenJson === null) return null;
    const token: Token = JSON.parse(tokenJson);
    return token;
  }

  /**
   * Clears the token from user session
   */
  public clearToken(): void {
    localStorage.removeItem('token');
    this._accessToken = null;
  }

  /**
   * Returns whether the refresh token is expired
   * @returns {Promise<boolean>} - Whether the refresh token is expired
   */
  public async isRefreshTokenExpired$(): Promise<boolean> {
    // Get refresh token
    const refresh = this.refreshToken;
    if (refresh === null) return Promise.resolve(true);

    // If saved refresh token is expired, return true
    if (this._jwtService.isTokenExpired(refresh)) {
      this.clearToken();
      return Promise.resolve(true);
    }

    // If token was already validated, return false
    if (this._refreshValid) return Promise.resolve(false);

    // If validating token, return current promise
    if (this._refreshValidPromise$) return this._refreshValidPromise$;

    // Validate refresh token with server
    const expired = firstValueFrom(
      this.http.post('auth/verify/', { token: refresh }).pipe(
        map(() => false),
        catchError(() => of(true)),
        tap((expired) => {
          this._refreshValid = !expired;
          this._refreshValidPromise$ = undefined;
        }),
      ),
    );
    this._refreshValidPromise$ = expired;
    return expired;
  }

  /**
   * Uses refresh token to update session token
   * Returns whether token refresh was successful
   * @return {Promise<boolean>} - Whether token refresh was successful
   */
  public async refreshToken$(): Promise<boolean> {
    // Get refresh token
    const refresh = this.refreshToken;

    // If no refresh token, return false
    if (refresh === null) return Promise.resolve(false);

    // If refreshing token, return current promise
    if (this._refreshingPromise$) return this._refreshingPromise$;

    // Create promise to avoid duplicates
    this._refreshingPromise$ = firstValueFrom(
      this.http.post<Token>('auth/refresh/', { refresh }).pipe(
        map((data: Token) => {
          this.saveToken(data);
          return true;
        }),
        catchError(() => {
          this.clearToken();
          return of(false);
        }),
        tap(() => (this._refreshingPromise$ = undefined)),
      ),
    );

    return this._refreshingPromise$;
  }
}
